import { ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmationModal from '../../common/DeleteConfirmationModal/DeleteConfirmationModal';
import Header from '../../common/Header/Header';
import { Buttons, Page, TableWrapper } from '../../common/TableWithButtonsRightLayout/TableWithButtonsRightLayout.styles';
import Button from '../../components/Button/Button';
import { Variant } from '../../components/Button/Button.types';
import Table from '../../components/Table/Table';
import { showToast } from '../../components/Toast/Toast';
import { useEditUser, useUsers } from '../../services/user/user.service';
import { User } from '../../services/user/user.types';
import { useFetchErrorToast } from '../../utils/useFetchErrorToasts/useFetchErrorToast';
import { genericErrorShowToastOptions } from '../../utils/useGenericErrorMessage/useGenericErrorMessage';
import useNavigateWithToast from '../../utils/useNavigateWithToast/useNavigateWithToast';

const Users: FC = () => {
  const navigate = useNavigate();
  const navigateWithToast = useNavigateWithToast();
  const gridRef = useRef<AgGridReact>(null);
  const { data: activeUsersData, isError: activeUsersError, refetch: refetchActiveUsers } = useUsers(true, false);
  const { data: deletedUsersData, isError: deletedUsersError, refetch: refetchDeletedUsers } = useUsers(true, true);
  const activeUsers = activeUsersData?.data || [];
  const deletedUsers = deletedUsersData?.data || [];
  const combinedUsers = activeUsers.concat(deletedUsers);
  const [selectedRow, setSelectedRow] = useState<User | undefined>(undefined);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { mutate, isLoading } = useEditUser(selectedRow?.id);

  const handleRowSelection = () => { 
    const selectedRows = gridRef?.current?.api.getSelectedRows();
    setSelectedRow(selectedRows?.[0]);
  }

  const handleEditClick = () => { 
    navigate(`/tables/users/${selectedRow?.loginId}`)
  }

  const handleAddNew = () => { 
    navigate(`/tables/users/add`);
  }

  const handleDelete = (event: { preventDefault: () => void}) => {
    event.preventDefault();
    mutate(
      {
        description: selectedRow?.description as string,
        loginId: selectedRow?.loginId as string,
        security: selectedRow?.security as number,
        webAccess : selectedRow?.webAccess as number,
        type: selectedRow?.type as string,
        deleted: true,
      },
      {
        onSuccess: () => {
          navigateWithToast(`/tables/users`, {
            label: 'User has been deleted',
            id: 'userDeleteSuccess',
          });
          refetchActiveUsers();
          refetchDeletedUsers();
          closeDeleteModal();
          setSelectedRow(undefined);
        },
        onError: (e) => { 
          showToast(genericErrorShowToastOptions);
          closeDeleteModal();
      }
      }
    );
  };

  const closeDeleteModal = () => { 
    setDeleteModalOpen(false);
  }

  const openDeleteModal = () => { 
    setDeleteModalOpen(true);
  }

  useFetchErrorToast([
    { isError: activeUsersError, label: 'Error fetching active users', id: 'activeUsersFetchError' },
    { isError: deletedUsersError, label: 'Error fetching deleted users', id: 'deletedUsersFetchError' },
  ]);



  return (
    <>
      <Header breadcrumb={{ to: '/tables', label: 'All tables' }} title='Users' />
      <Page>
        <TableWrapper>
          <Table
            rowData={(activeUsersData && deletedUsersData) ? combinedUsers : []}
            ref={gridRef}
            returnedRecords={(activeUsersData && deletedUsersData) ? combinedUsers.length : 0}
            height={'100%'}
            rowSelection='single'
            onRowSelected={handleRowSelection}
            columnDefs={[
              {
                minWidth: 48,
                maxWidth: 80,
                field: '',
                headerName: '',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
                flex: 1,
              },
              { minWidth: 80, flex: 1, field: 'id', headerName: 'User Number' },
              { minWidth: 112, flex: 1, field: 'description', headerName: 'Full Name' },
              { minWidth: 80, flex: 1, field: 'loginId', headerName: 'Login ID' },
              { minWidth: 80, flex: 1, field: 'security', headerName: 'Security' },
              { minWidth: 80, flex: 1, field: 'webAccess', headerName: 'Web Access' },
              { minWidth: 80, flex: 1, field: 'type', headerName: 'Type' },
              {
                minWidth: 80,
                field: 'deleted',
                headerName: 'Deleted',
                resizable: false,
                flex: 1,
                valueFormatter: (params) => (!params.value ? 'N' : 'Y'),
                filterValueGetter: (params: ValueGetterParams) => (!params.getValue('deleted') ? 'N' : 'Y'),
              },
            ]}
          />
        </TableWrapper>
        <Buttons>
            <Button label='Add New' testId='addNewUser' variant={Variant.Primary} onClick={handleAddNew} />
            <Button 
              label='Edit' 
              testId='editUser'
              variant={Variant.Secondary}
              onClick={handleEditClick}
              disabled={selectedRow === undefined || selectedRow?.deleted === true}
            />
            <Button 
              label='Delete' 
              testId='deleteUser'
              variant={Variant.Secondary} 
              onClick={openDeleteModal}
              disabled={selectedRow === undefined || selectedRow?.deleted === true}
            />
        </Buttons>
        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={handleDelete}
          cancelBtnTestId='cancelDeleteUserModal'
          confirmBtnTestId='confirmDeleteUserModal'
          title={'Delete User Record'}
          message={'Are you sure you want to delete this user?'}
          isLoading={isLoading}
      />
      </Page>
    </>
  );
}

export default Users;